import { useConfirmDialog } from '@vueuse/core'
import { ref } from 'vue'
import type { Component } from 'vue'
import type { WdsIconNameEnum } from '@worksome/wds-components'

export type ConfirmModalTemplate = 'confirmation' | 'warning' | 'danger' | 'info' | 'delete'
export type ConfirmModalSize = 'sm' | 'md' | 'lg'
export type ConfirmModalButtonVariant =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'delete'
  | 'warning'
  | 'success'
  | 'info'
export type ConfirmModalIcon = `${WdsIconNameEnum}`

export interface ConfirmModalOptions {
  actionName?: string
  title: string
  note?: string
  description?: string
  template?: ConfirmModalTemplate
  icon?: ConfirmModalIcon
  iconColor?: string
  confirmText?: string
  cancelText?: string
  hideCancel?: boolean
  confirmVariant?: ConfirmModalButtonVariant
  size?: ConfirmModalSize
  teleportTarget?: string
  component?: Component
  componentProps?: Record<string, unknown>
  onConfirm?: () => Promise<void> | void
  loadingTitle?: string
}

// Module-scope state: created once on first import, shared by every caller.
const { isRevealed, reveal, confirm, cancel, onReveal } = useConfirmDialog<
  ConfirmModalOptions,
  void,
  void
>()

const options = ref<ConfirmModalOptions>()
const isLoading = ref(false)

onReveal((revealedOptions) => {
  options.value = revealedOptions
})

async function openConfirmModal(modalOptions: ConfirmModalOptions): Promise<boolean> {
  const { isCanceled } = await reveal(modalOptions)

  return !isCanceled
}

async function handleConfirm(): Promise<void> {
  if (isLoading.value) return
  if (!options.value?.onConfirm) {
    confirm()
    return
  }
  isLoading.value = true
  try {
    await options.value.onConfirm()
    confirm()
  } catch {
    cancel()
  } finally {
    isLoading.value = false
  }
}

function handleCancel(): void {
  if (isLoading.value) return

  cancel()
}

export function useConfirmModal() {
  return {
    isRevealed,
    options,
    isLoading,
    openConfirmModal,
    handleConfirm,
    handleCancel,
  }
}
